import {
  IAccount,
  IAccountCreateRequest,
  IAccountUpdateRequest,
  IBenefitSelectionRule,
} from "types/Account/Account";
import {
  IAccountResponse,
  IAllAccountResponse,
} from "types/Account/AccountResponse";
import { IPayerAccountFees } from "types/Account/AcountFees";

import { api } from ".";

const accountQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    getCurrent: builder.query<IAccountResponse, null | void>({
      query: () => "accounts/current",
    }),
    getAccountById: builder.query<IAccountResponse, string>({
      query: (id: string) => `accounts/${id}`,
      // CLE-2683 — the edit form seeds itself from this response, and the benefit rules save is a
      // wipe-and-rewrite. Without invalidation a cached copy re-seeds the form with the pre-save
      // rules, and the next save writes that stale set back, silently dropping whatever was added.
      providesTags: (_result, _error, id) => [{ type: "Account" as const, id }],
    }),
    getAccountFeesById: builder.query<IAccountResponse, string>({
      query: (id: string) => `accounts/accountFees/${id}`,
    }),
    // CLE-2670 — archived accounts are excluded by default. Only the hospitals table passes
    // includeArchived, so it can offer a "show archived" view to restore one; everything else
    // (account switcher, user account picker) keeps calling this with no argument.
    getAllAccounts: builder.query<
      IAllAccountResponse,
      { includeArchived?: boolean } | null | void
    >({
      query: (params) =>
        params?.includeArchived
          ? "accounts/all?includeArchived=true"
          : "accounts/all",
    }),
    setAccountArchived: builder.mutation<
      { data: { id: string; name: string; accountStatus: string } },
      { id: string; archived: boolean }
    >({
      query: ({ id, archived }) => ({
        url: `accounts/${id}/archived`,
        method: "PATCH",
        body: { archived },
      }),
    }),
    createAccount: builder.mutation<IAccount, IAccountCreateRequest>({
      query: (newAccount) => ({
        url: "accounts",
        method: "POST",
        body: newAccount,
      }),
    }),
    updateAccount: builder.mutation<IAccount, IAccountUpdateRequest>({
      query: (updatedAccount) => ({
        url: `accounts/${updatedAccount.id}`,
        method: "PUT",
        body: updatedAccount,
      }),
    }),
    upsertPayerAccountFees: builder.mutation<
      IPayerAccountFees[],
      { accountId: string; payerAccountFees: IPayerAccountFees[] }
    >({
      query: ({ accountId, payerAccountFees }) => ({
        url: `accounts/payerAccountFees`,
        method: "POST",
        body: { accountId, payerAccountFees },
      }),
    }),
    // CLE-2683 — the account's benefit selection rules, replaced as a whole set.
    upsertBenefitSelectionRules: builder.mutation<
      { data: IBenefitSelectionRule[]; message: string },
      { accountId: string; rules: IBenefitSelectionRule[] }
    >({
      query: ({ accountId, rules }) => ({
        url: `accounts/benefitSelectionRules`,
        method: "POST",
        body: { accountId, rules },
      }),
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "Account" as const, id: accountId },
      ],
    }),
  }),
});

export const {
  useGetCurrentQuery,
  useGetAllAccountsQuery,
  useSetAccountArchivedMutation,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useGetAccountByIdQuery,
  useGetAccountFeesByIdQuery,
  useUpsertPayerAccountFeesMutation,
  useUpsertBenefitSelectionRulesMutation,
} = accountQueries;
